import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { FileBasedSettings, FILEMODE } from 'app/model/dataflow/dataset-settings/file-based-settings';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { BooleanDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataFlowExpressionUtils } from '../expression.util';
import { BaseFormatAdapter } from './base-format.adapter';

export abstract class FileBasedSettingsAdapter extends BaseFormatAdapter<FileBasedSettings> {
    protected async toSourceDSL(settings: FileBasedSettings): Promise<string[]> {
        const props: string[] = [];
        const model = settings.owner as DataFlowSource;
        props.push(`format: '${settings.getFormat()}'`);
        if (settings.container.expression) {
            if (LinkedServiceUtils.isBucketNameTypeInBackend(model.sourceLinkedService)) {
                props.push(`bucketName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else if (LinkedServiceUtils.isFileSystemTypeInBackend(model.sourceLinkedService)) {
                props.push(`fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else {
                props.push(`container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            }
        }
        if (settings.fileMode === FILEMODE.FILE) {
            if (settings.folderPath.expression) {
                props.push(`folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.folderPath)}`);
            }
            if (settings.fileName.expression) {
                props.push(`fileName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.fileName)}`);
            }
        }
        return props;
    }

    protected async toSinkDSL(settings: FileBasedSettings): Promise<string[]> {
        const props: string[] = [];
        const model = settings.owner as DataFlowSink;
        props.push(`format: '${settings.getFormat()}'`);
        if (settings.container.expression) {
            if (LinkedServiceUtils.isBucketNameTypeInBackend(model.sinkLinkedService)) {
                props.push(`bucketName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else if (LinkedServiceUtils.isFileSystemTypeInBackend(model.sinkLinkedService)) {
                props.push(`fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            } else {
                props.push(`container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.container)}`);
            }
        }
        if (settings.folderPath.expression) {
            props.push(`folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.folderPath)}`);
        }
        if (isTruthy(model.clearFolder)) {
            props.push(`truncate: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.clearFolder, BooleanDataFlowType.TYPE)}`);
        }
        return props;
    }

    protected async toSourceModel(owner: DataFlowSource, configs: Map<string, string>, handledConfigs: string[]): Promise<FileBasedSettings> {
        const settings = this._generateSetting(owner);
        settings.initialize(undefined);
        settings.fileMode = configs.has('wildcardPaths') ? FILEMODE.WILDCARD : FILEMODE.FILE;

        if (configs.has('container')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('container'), owner);
            handledConfigs.push('container');
        } else if (configs.has('fileSystem')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('fileSystem'), owner);
            handledConfigs.push('fileSystem');
        } else if (configs.has('bucketName')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('bucketName'), owner);
            handledConfigs.push('bucketName');
        }
        if (configs.has('folderPath')) {
            settings.folderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('folderPath'), owner);
            settings.fileMode = FILEMODE.FILE;
            handledConfigs.push('folderPath');
        }
        if (configs.has('fileName')) {
            settings.fileName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('fileName'), owner);
            settings.fileMode = FILEMODE.FILE;
            handledConfigs.push('fileName');
        }

        return settings;
    }

    protected async toSinkModel(owner: DataFlowSink, configs: Map<string, string>, handledConfigs: string[]): Promise<FileBasedSettings> {
        const settings = this._generateSetting(owner);
        settings.initialize(undefined);

        if (configs.has('container')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('container'), owner);
            handledConfigs.push('container');
        } else if (configs.has('fileSystem')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('fileSystem'), owner);
            handledConfigs.push('fileSystem');
        } else if (configs.has('bucketName')) {
            settings.container = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('bucketName'), owner);
            handledConfigs.push('bucketName');
        }
        if (configs.has('folderPath')) {
            settings.folderPath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('folderPath'), owner);
            handledConfigs.push('folderPath');
        }

        return settings;
    }

    protected abstract _generateSetting(owner: DataFlowSource | DataFlowSink): FileBasedSettings;
}
